import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { SelectedItem, TabletopState } from '../types';
import { CardStackDirection } from '../utils/cardStacking';

interface ContextActionBarProps {
  selectedItem: SelectedItem | null;
  selectedHandCardId: string | null;
  selectedCardIds: string[];
  tabletopState: TabletopState;
  onDeckDrawOne: (deckId: string) => void;
  onDeckSearch: (deckId: string) => void;
  onDeckShuffle: (deckId: string) => void;
  onDeckShare: (deckId: string) => void;
  onCardsFlip: (cardIds: string[]) => void;
  onCardsRotate: (cardIds: string[]) => void;
  onCardsToHand: (cardIds: string[]) => void;
  onCardInspect: (cardId: string) => void;
  onCardsStack: (direction: CardStackDirection) => void;
  onDiceRoll: (diceId: string) => void;
  onCoinFlip: (coinId: string) => void;
  onTokenEdit: (tokenId: string) => void;
  onDelete: (type: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string) => void;
  onClear: () => void;
  topOffsetPx?: number;
}

type Action = {
  key: string;
  label: string;
  icon: string;
  shortcut?: string;
  danger?: boolean;
  onClick: () => void;
};

function useCanShowKeyboardHints() {
  const [canShowKeyboardHints, setCanShowKeyboardHints] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const handleChange = () => setCanShowKeyboardHints(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return canShowKeyboardHints;
}

export default function ContextActionBar({
  selectedItem,
  selectedHandCardId,
  selectedCardIds,
  tabletopState,
  onDeckDrawOne,
  onDeckSearch,
  onDeckShuffle,
  onDeckShare,
  onCardsFlip,
  onCardsRotate,
  onCardsToHand,
  onCardInspect,
  onCardsStack,
  onDiceRoll,
  onCoinFlip,
  onTokenEdit,
  onDelete,
  onClear,
  topOffsetPx
}: ContextActionBarProps) {
  const canShowKeyboardHints = useCanShowKeyboardHints();
  const positionStyle: CSSProperties | undefined = topOffsetPx === undefined ? undefined : { top: `${topOffsetPx}px` };
  let title = 'Selection';
  let subtitle = '';
  let actions: Action[] = [];

  if (selectedHandCardId) {
    const card = tabletopState.hand[selectedHandCardId];
    title = card?.name || 'Hand card';
    subtitle = 'In your hand';
    actions = [
      { key: 'clear', label: 'Clear', icon: '×', shortcut: 'Esc', onClick: onClear }
    ];
  } else if (selectedItem?.type === 'deck') {
    const deck = tabletopState.decks[selectedItem.id];
    if (!deck) return null;
    title = deck.name || 'Deck';
    subtitle = `${deck.cards.length} card${deck.cards.length === 1 ? '' : 's'}`;
    actions = [
      { key: 'draw', label: 'Draw', icon: '↓', shortcut: 'D', onClick: () => onDeckDrawOne(deck.id) },
      { key: 'search', label: 'Search', icon: '⌕', shortcut: 'G', onClick: () => onDeckSearch(deck.id) },
      { key: 'shuffle', label: 'Shuffle', icon: '⟳', shortcut: 'S', onClick: () => onDeckShuffle(deck.id) },
      { key: 'share', label: 'Share', icon: '⇪', onClick: () => onDeckShare(deck.id) },
      { key: 'delete', label: 'Delete', icon: '⌫', shortcut: 'Del', danger: true, onClick: () => onDelete('deck', deck.id) }
    ];
  } else if (selectedItem?.type === 'card') {
    const card = tabletopState.cards[selectedItem.id];
    if (!card) return null;
    title = card.name || 'Card';
    const multiCardCount = selectedCardIds.length > 1 ? selectedCardIds.length : 0;
    const cardActionIds = multiCardCount > 1 ? selectedCardIds : [card.id];
    subtitle = multiCardCount > 1 ? `${multiCardCount} cards selected` : card.isFaceUp ? 'Face up' : 'Face down';
    actions = [
      { key: 'inspect', label: 'Inspect', icon: '⌕', shortcut: 'Z', onClick: () => onCardInspect(card.id) },
      { key: 'flip', label: 'Flip', icon: '↔', shortcut: 'F', onClick: () => onCardsFlip(cardActionIds) },
      { key: 'rotate', label: 'Rotate', icon: '↻', shortcut: 'R', onClick: () => onCardsRotate(cardActionIds) },
      { key: 'hand', label: 'To Hand', icon: '↓', shortcut: 'H', onClick: () => onCardsToHand(cardActionIds) },
      { key: 'delete', label: 'Delete', icon: '⌫', shortcut: 'Del', danger: true, onClick: () => cardActionIds.forEach(cardId => onDelete('card', cardId)) }
    ];
    if (multiCardCount > 1) {
      actions = [
        { key: 'stack', label: 'Stack', icon: '▣', shortcut: 'S', onClick: () => onCardsStack('compact') },
        { key: 'fan-right', label: 'Fan Right', icon: '▸', shortcut: 'Opt + →', onClick: () => onCardsStack('right') },
        { key: 'fan-down', label: 'Fan Down', icon: '▾', shortcut: 'Opt + ↓', onClick: () => onCardsStack('down') },
        ...actions
      ];
    }
  } else if (selectedItem?.type === 'dice') {
    const dice = tabletopState.dice[selectedItem.id];
    if (!dice) return null;
    title = 'Dice';
    subtitle = 'Rollable set';
    actions = [
      { key: 'roll', label: 'Roll', icon: '⚄', shortcut: 'R', onClick: () => onDiceRoll(dice.id) },
      { key: 'delete', label: 'Delete', icon: '⌫', shortcut: 'Del', danger: true, onClick: () => onDelete('dice', dice.id) }
    ];
  } else if (selectedItem?.type === 'token') {
    const token = tabletopState.tokens?.[selectedItem.id];
    if (!token) return null;
    title = token.text || 'Token';
    subtitle = 'Counter marker';
    actions = [
      { key: 'edit', label: 'Edit', icon: '✎', shortcut: 'E', onClick: () => onTokenEdit(token.id) },
      { key: 'delete', label: 'Delete', icon: '⌫', shortcut: 'Del', danger: true, onClick: () => onDelete('token', token.id) }
    ];
  } else if (selectedItem?.type === 'coin') {
    const coin = tabletopState.coins?.[selectedItem.id];
    if (!coin) return null;
    title = 'Coin';
    subtitle = coin.side === 'heads' ? 'Heads' : 'Tails';
    actions = [
      { key: 'flip', label: 'Flip', icon: '↔', shortcut: 'F', onClick: () => onCoinFlip(coin.id) },
      { key: 'delete', label: 'Delete', icon: '⌫', shortcut: 'Del', danger: true, onClick: () => onDelete('coin', coin.id) }
    ];
  } else {
    return null;
  }

  return (
    <div
      className="context-action-bar pointer-events-auto fixed left-1/2 top-[5.2rem] z-40 flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-3 px-3 py-2"
      style={positionStyle}
    >
      <div className="min-w-0 border-r border-slate-900/10 pr-3 dark:border-white/10">
        <p className="max-w-[220px] truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</p>
        <p className="truncate text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      <div className="flex items-center gap-1.5 overflow-x-auto">
        {actions.map(action => (
          <button
            key={action.key}
            type="button"
            onClick={action.onClick}
            className={`context-action-button ${action.danger ? 'context-action-button-danger' : ''}`}
            aria-label={action.label}
          >
            <span aria-hidden="true">{action.icon}</span>
            <span className="hidden sm:inline">{action.label}</span>
            {canShowKeyboardHints && action.shortcut && (
              <kbd className="context-action-kbd" aria-label={`Keyboard shortcut ${action.shortcut}`}>
                {action.shortcut}
              </kbd>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
